import { Injectable } from '@angular/core';
import {
    CanActivate,
    Router,
    ActivatedRouteSnapshot,
    RouterStateSnapshot,
    UrlTree,
} from '@angular/router';
import { StorageService } from '../storage.service';
import { AuthService } from '../auth.service';
import { TenantService } from 'app/core/tenant/tenant.service';

@Injectable({ providedIn: 'root' })
export class RoleGuard implements CanActivate {
    isTenant: boolean = true;
    currentUser: any = null;
    userName: string = 'Guest';
    role: string = 'Guest';
    permissions: string[] = [];

    constructor(
        private router: Router,
        private key: StorageService,
        private authService: AuthService,
        private tenantService: TenantService
    ) {}

    // Method helper untuk cek limited access user
    private isLimitedAccessUser(permissions: string[]): boolean {
        const allowedPermissions = [
            'report.parking',
            'parking.update',
            'report.void',
        ];
        return (
            permissions.length > 0 &&
            permissions.length <= 3 &&
            permissions.every((perm) => allowedPermissions.includes(perm))
        );
    }

    // Method helper untuk cek apakah route adalah report-parking
    private isReportParkingRoute(url: string): boolean {
        return (
            url.includes('/admin/report-parking') ||
            url.includes('/report-parking')
        );
    }

    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): boolean | UrlTree {
        console.log('RoleGuard: canActivate called for URL:', state.url);
        const userEnc = localStorage.getItem('user');

        if (!userEnc) {
            console.warn('RoleGuard: No user found in localStorage, redirecting to sign-in');
            return this.router.createUrlTree(['/sign-in']);
        }

        try {
            this.currentUser = this.authService.getCurrentUser();

            if (this.currentUser) {
                // console.log('User logged in', this.currentUser);
                this.userName = this.currentUser.name;
                this.role = this.currentUser.role;
                this.isTenant = this.currentUser.is_tenant;
                this.permissions = this.currentUser.permissions || [];
            } else {
                console.log('RoleGuard: No currentUser from AuthService');
            }

            const decrypted = this.key.decryptionAES(userEnc);
            const user = JSON.parse(decrypted);

            const isTenant = user.is_tenant === true;
            const hasParkingAccess = user.has_parking_access === true;
            const permissions = user.permissions || [];
            const allowedRoles = route.data['roles'] as string[];
            const serviceType = user.service_type || 'parking';
            const tenantName = this.tenantService.tenantName;

            console.log('RoleGuard Context:', {
                isTenant,
                hasParkingAccess,
                serviceType,
                tenantName,
                url: state.url
            });

            // ===== CHECK LIMITED ACCESS USER =====
            // Jika user hanya punya report.parking dan/atau parking.update
            if (this.isLimitedAccessUser(permissions)) {
                // Jika sedang akses /redirect atau bukan report-parking route
                if (
                    state.url === '/redirect' ||
                    !this.isReportParkingRoute(state.url)
                ) {
                    console.log('RoleGuard: Redirecting limited user to report-parking');
                     // Note: Ideally this should also be dynamic, but keeping hardcoded for safety unless generic report path exists
                     return this.router.createUrlTree([`/${tenantName}/${serviceType}/admin/report-parking`]); // Or `/${tenantName}/${serviceType}/admin/report-parking`
                }
                // Jika sudah di route report-parking, allow access
                if (this.isReportParkingRoute(state.url)) {
                    return true;
                }
            }

            // ===== AUTO REDIRECT ENDPOINT =====
            if (state.url === '/redirect') {
                // Check limited user first (sudah di handle di atas)
                if (isTenant && hasParkingAccess) {
                    console.log(`RoleGuard: Redirecting Tenant/POS to /${tenantName}/${serviceType}/tenant`);
                    // Redirect tenant (POS) to generic path
                    return this.router.createUrlTree([`/${tenantName}/${serviceType}/tenant`]);
                } else {
                    console.log(`RoleGuard: Redirecting Admin to /${tenantName}/${serviceType}/admin/dashboard`);
                     // Redirect admin to generic path
                    return this.router.createUrlTree([`/${tenantName}/${serviceType}/admin/dashboard`]);
                }
            }

            // ===== TENANT SECTION =====
            if (allowedRoles?.includes('tenant')) {
                if (isTenant && hasParkingAccess) return true;
                return this.router.createUrlTree([`/${tenantName}/${serviceType}/admin/dashboard`]);
            }

            // ===== ADMIN SECTION =====
            if (allowedRoles?.includes('admin')) {
                // Cek lagi untuk limited user
                if (this.isLimitedAccessUser(permissions)) {
                    // Hanya allow jika route-nya report-parking
                    if (this.isReportParkingRoute(state.url)) {
                        return true;
                    }
                    // Jika bukan report-parking, redirect ke report-parking
                    return this.router.createUrlTree(['/admin/report-parking']);
                }

                // Normal admin check
                if (!isTenant || (isTenant && !hasParkingAccess)) return true;
                return this.router.createUrlTree([`/${tenantName}/${serviceType}/tenant`]);
            }

            return true;
        } catch (error) {
            console.error('RoleGuard error:', error);
            return this.router.createUrlTree(['/sign-in']);
        }
    }
}
